import { PropsWithChildren, ReactNode, useMemo } from 'react';
import { useFormikContext } from 'formik';
import SubmitButton from './SubmitButton';
import { Button, Tooltip } from '@mui/material';
import ModalAnnouncementCenter from './ModalAnnouncementCenter';
import { isFormikErrorsEmpty } from '@hooks/useFormikValues';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import { queueModal } from '@components/Modals/modalSlice';
import { parseFormikErrors, unsavedChangesMessage } from '@forms/types';
import ErrorIcon from '@mui/icons-material/ErrorTwoTone';
import { selectActiveFormState } from '@/ConsignmentDetails/formStateSlice';

import styles from './ApplicationModal.module.css';

interface FormikModalHeaderProps extends PropsWithChildren {
	/** Name of the form element to submit on click */
	formName: string;
	/** Handler for closing the modal */
	onClose: () => void;
	/** Text to display on the modal title bar */
	title: string | ReactNode;
	/** Button actions to render on the bar */
	actions?: ReactNode;
	/** Text to display on the submit button. Default: 'Save' */
	submitButtonText?: string;
	/** Hovering the save button with pending changes will display this parameters */
	submitButtonMessage?: string;
	/** Whether the form is currently processing (submitting or thinking) */
	isProcessing?: boolean;
	/** Override the default Are You Sure confirmation dialog text */
	areYouSurePrompt?: string;
	/** Override default behavior of requiring at least 1 form change */
	enableSubmitAlways?: boolean;
	/** Hide the submit button for a form with nothing to submit; the modal then also closes without the unsaved changes confirmation */
	hideSubmit?: boolean;
}

export default function FormikModalHeader({ enableSubmitAlways, isProcessing, hideSubmit, ...props }: FormikModalHeaderProps) {
	const { dirty, isSubmitting, errors } = useFormikContext();
	const appDispatch = useAppDispatch();

	const isFormProcessing = useMemo(() => {
		return isSubmitting || isProcessing;
	}, [isSubmitting, isProcessing]);

	const onCloseClick = () => {
		if (dirty && !hideSubmit) {
			appDispatch(
				queueModal({
					Component: 'confirmPopup',
					Props: {
						prompt: props.areYouSurePrompt ? props.areYouSurePrompt : unsavedChangesMessage,
						confirmedAction: () => {
							props.onClose();
						}
					}
				})
			);
		} else {
			props.onClose();
		}
	};

	const errorDisplay = useMemo(() => {
		if (isFormikErrorsEmpty(errors)) return null;

		const errorList = parseFormikErrors(errors);
		return (
			<ul className={styles['error-tooltip']}>
				{errorList.map((error, index) => (
					<li key={`error-item-${String(index)}`}>{error}</li>
				))}
			</ul>
		);
	}, [errors]);

	const isSubmitDisabled = useMemo(() => {
		if (enableSubmitAlways) return false;
		return !dirty || !isFormikErrorsEmpty(errors);
	}, [dirty, enableSubmitAlways, errors]);

	return (
		<>
			<div
				id={'draggable-window'}
				className={styles.header}
			>
				{props.title}
				{props.children}
				<div className={styles['actions']}>
					{!isFormikErrorsEmpty(errors) && dirty && (
						<Tooltip title={errorDisplay}>
							<ErrorIcon className={styles['error-icon']} />
						</Tooltip>
					)}
					{props.actions && props.actions}
					{!hideSubmit && (
						<SubmitButton
							formName={props.formName}
							isSubmitting={isFormProcessing}
							isDisabled={isSubmitDisabled}
							buttonText={props.submitButtonText ? props.submitButtonText : 'Save'}
							submitMessage={props.submitButtonMessage ? props.submitButtonMessage : 'Save Changes'}
						/>
					)}
					<Button
						variant='outlined'
						sx={{ marginRight: '4px' }}
						onClick={onCloseClick}
					>
						Close
					</Button>
				</div>
			</div>

			<ModalAnnouncementCenter />
		</>
	);
}

/** Creating a separate component to be used by Consignment Details which breaks the Formik Context */
export function CsnFormikModalHeader({ enableSubmitAlways, isProcessing, ...props }: FormikModalHeaderProps) {
	const formState = useAppSelector(selectActiveFormState);
	const dirty = formState?.dirty ?? false;
	const isSubmitting = formState?.isSubmitting ?? false;
	const errors = useMemo(() => formState?.errors ?? {}, [formState?.errors]);
	const appDispatch = useAppDispatch();

	const isFormProcessing = useMemo(() => {
		return isSubmitting || isProcessing;
	}, [isSubmitting, isProcessing]);

	const onCloseClick = () => {
		if (dirty) {
			appDispatch(
				queueModal({
					Component: 'confirmPopup',
					Props: {
						prompt: props.areYouSurePrompt ? props.areYouSurePrompt : unsavedChangesMessage,
						confirmedAction: () => {
							props.onClose();
						}
					}
				})
			);
		} else {
			props.onClose();
		}
	};

	const errorDisplay = useMemo(() => {
		if (isFormikErrorsEmpty(errors)) return null;

		const errorList = parseFormikErrors(errors);
		return (
			<ul className={styles['error-tooltip']}>
				{errorList.map((error, index) => (
					<li key={`error-item-${String(index)}`}>{error}</li>
				))}
			</ul>
		);
	}, [errors]);

	const isSubmitDisabled = useMemo(() => {
		if (enableSubmitAlways) return false;
		return !dirty || !isFormikErrorsEmpty(errors);
	}, [dirty, enableSubmitAlways, errors]);

	return (
		<>
			<div
				id={'draggable-window'}
				className={styles.header}
			>
				{props.title}
				{props.children}
				<div className={styles['actions']}>
					{!isFormikErrorsEmpty(errors) && dirty && (
						<Tooltip title={errorDisplay}>
							<ErrorIcon className={styles['error-icon']} />
						</Tooltip>
					)}
					{props.actions && props.actions}
					<SubmitButton
						formName={props.formName}
						isSubmitting={isFormProcessing}
						isDisabled={isSubmitDisabled}
						buttonText={props.submitButtonText ? props.submitButtonText : 'Save'}
						submitMessage={props.submitButtonMessage ? props.submitButtonMessage : 'Save Changes'}
					/>
					<Button
						variant='outlined'
						sx={{ marginRight: '4px' }}
						onClick={onCloseClick}
					>
						Close
					</Button>
				</div>
			</div>

			<ModalAnnouncementCenter />
		</>
	);
}
